import type { SortableListItemId } from "./SortableList.types";

export function reorderSortableListItems<Item>(
  items: readonly Item[],
  sourceIndex: number,
  targetIndex: number,
): Item[] {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(sourceIndex, 1);

  if (movedItem === undefined) {
    return nextItems;
  }

  nextItems.splice(targetIndex, 0, movedItem);

  return nextItems;
}

type SortableListReorder = {
  sourceId: SortableListItemId;
  targetId: SortableListItemId;
  sourceIndex: number;
  targetIndex: number;
};

export type SortableListSourceWithIndices = {
  initialIndex: number;
  index: number;
};

export function hasSortableIndices(
  source: unknown,
): source is SortableListSourceWithIndices {
  if (!source || typeof source !== "object") {
    return false;
  }

  const maybeSource = source as Partial<SortableListSourceWithIndices>;

  return (
    typeof maybeSource.initialIndex === "number" &&
    typeof maybeSource.index === "number"
  );
}

export function getSortableListReorder(
  itemIds: readonly SortableListItemId[],
  sourceId: SortableListItemId | undefined,
  targetId: SortableListItemId | undefined,
  sortableSource?: SortableListSourceWithIndices,
): SortableListReorder | null {
  if (sourceId === undefined || targetId === undefined) {
    return null;
  }

  const sourceIndex = sortableSource
    ? sortableSource.initialIndex
    : itemIds.indexOf(sourceId);
  const targetIndex = sortableSource
    ? sortableSource.index
    : itemIds.indexOf(targetId);

  if (
    sourceIndex < 0 ||
    sourceIndex >= itemIds.length ||
    targetIndex < 0 ||
    targetIndex >= itemIds.length ||
    targetIndex === sourceIndex
  ) {
    return null;
  }

  return {
    sourceId: itemIds[sourceIndex] ?? sourceId,
    targetId: itemIds[targetIndex] ?? targetId,
    sourceIndex,
    targetIndex,
  };
}
